
import React from "react";
import { CacheManager, Utils, resolveTemplateColors } from "../../../../utils";
import Definition from "../definition";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { IMember, IProfileWebsite } from "../../../../services";
import { Classname, DateHelper, StringHelper } from "@rivtn/noseur";
import { useComponentStyle } from "../../../../hooks/useComponentStyle";
import Home from "./Home";
import Merch from "./Merch";
import Gallery from "./Gallery";
import Schedule from "./Schedule";
import TeamHistory from "./TeamHistory";
import Achievement from "./Achievement";
import { i18nManager } from "../../../../i18n";
import LanguageDropdown from "../../../../shared/LanguageDropdown";

export interface LandingProps {
    basePath: string;
    publicId: string;
    website: IProfileWebsite;
}

function Landing(props: LandingProps) {

    const labels = i18nManager.Labels;
    const [cart,] = React.useState([]);
    const ddRef = React.useRef(null) as any;
    const today = DateHelper.getStartOfDay();
    const { website, basePath, publicId } = props;
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [nameFirstLetter, ...nameRemain] = website.display_name.split("");
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [profile, setProfile] = React.useState<IMember | undefined>(CacheManager.get(CacheManager.PROFILE_KEY) as IMember);
    const colors = resolveTemplateColors(Definition, website.design_settings?.scheme_settings?.colors, searchParams);
    useComponentStyle(`
        :root {
            --red: ${colors.danger};
            --muted: ${colors.text_muted};
            --green: ${colors.success};
            --accent: ${colors.accent};
            --text: ${colors.text};
            --dark: ${colors.background};
            --dark2: ${colors.background_alt};
            --dark3: ${colors.surface};
            --dark4: ${colors.surface_alt};
            --gold: ${colors.primary};
            --header-bg: ${colors.header_background};
            --gold-light: ${colors.primary_light};
            --header-h: 72px;
        }
    `);

    React.useEffect(() => {
        Utils.updateMeta({
            title: website.display_name,
            desc: website.profile_detail,
            logo: website.media?.LOGO?.[0]
        });
    }, [website]);

    return (<>
        <header className={styles["hdr"]}>
            <ReactRouterDom.NavLink to={`/${basePath}`} className={styles["hdr-logo"]} style={{ textDecoration: "none" }}>
                {(website.media?.LOGO?.length) && (<img className={styles["hdr-crest"]} src={website.media?.LOGO?.[website.extra_data?.media?.LOGO?.active ?? 0]} alt={website.display_name} />)}
                <span>{nameFirstLetter}<span>{nameRemain}</span></span>
            </ReactRouterDom.NavLink>
            <nav className={styles["hdr-nav"]}>
                {Utils.entriesInCustomOrder(website.website_pages).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (
                    <ReactRouterDom.NavLink end key={page} to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                ))}
            </nav>
            <div className={styles["hdr-right"]}>
                {cart.length > 0 && (
                    <span style={{ fontSize: "0.8rem", color: "var(--gold)" }}><i className="fa fa-cart-shopping" /> {cart.length}</span>
                )}
                <LanguageDropdown />
                {(website.extra_data.enable_visitor_account_management) && ((!profile) ? (
                    <ReactRouterDom.NavLink className={styles["btn-signin"]} to={process.env.REACT_APP_PATULCIUS_AUTH_URL ?? "http://127.0.0.1:3005/oauth"}>{labels.auth.sign_in}</ReactRouterDom.NavLink>
                ) : (
                    <div className={styles["dropdown-wrap"]} ref={ddRef}>
                        <img className={styles["avatar-btn"]} onClick={() => setShowDropdown((v: boolean) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                        {showDropdown && (
                            <div className={styles["dropdown"]}>
                                <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"} onClick={() => setShowDropdown(false)}><i className="fa fa-chart-line" /> {labels.auth.dashboard}</ReactRouterDom.NavLink>
                                <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"} onClick={() => setShowDropdown(false)}><i className="fa fa-gear" /> {labels.landing.my_tournaments}</ReactRouterDom.NavLink>
                                <ReactRouterDom.NavLink target="_blank" className={styles["dd-item"]} to={"https://omnehost.com/tournaments"} onClick={() => setShowDropdown(false)}><i className="fa fa-chart-line" /> {labels.landing.join_tournaments}</ReactRouterDom.NavLink>
                                <hr className={styles["dd-divider"]} />
                                <button className={Classname.build(styles["dd-item"], styles["logout"])} onClick={() => {
                                    CacheManager.clear();
                                    setProfile(undefined);
                                }}><i className="fa fa-right-from-bracket" /> {labels.auth.sign_out}</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </header>
        <main className={styles["page"]}>
            <ReactRouterDom.Routes>
                <ReactRouterDom.Route path="/merch" element={<Merch website={website} />} />
                <ReactRouterDom.Route path="/gallery" element={<Gallery website={website} />} />
                <ReactRouterDom.Route path="/team_history" element={<TeamHistory website={website} />} />
                <ReactRouterDom.Route path="/achievement" element={<Achievement website={website} />} />
                <ReactRouterDom.Route path="/schedule" element={<Schedule website={website} publicId={publicId} />} />
                <ReactRouterDom.Route path="/" element={<Home website={website} basePath={basePath} publicId={publicId} />} />
            </ReactRouterDom.Routes>
        </main>
        <footer className={styles["footer"]}>
            <div className={styles["footer-inner"]}>
                <div className={styles["footer-brand"]}>
                    <div className={styles["footer-logo"]}>{website.owns_copyright ? website.display_name : labels.omnehost}</div>
                    <p className={styles["footer-desc"]}>{website.owns_copyright ? website.profile_detail : labels.omnehost_detail}</p>
                </div>
                <div>
                    <div className={styles["footer-col-title"]}>{labels.landing.navigation}</div>
                    <ul className={styles["footer-links"]}>
                        {Utils.entriesInCustomOrder(website.website_pages).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (<li key={page}>
                            <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build({ [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                        </li>))}
                    </ul>
                </div>
                <div>
                    <div className={styles["footer-col-title"]}>{labels.landing.connect}</div>
                    <ul className={styles["footer-links"]}>
                        {Object.entries(website.member.social_accounts ?? {}).map(([social, username]) => (<li key={social}>
                            <ReactRouterDom.NavLink target="_blank" to={Utils.getSocialLink(social, username)}>{StringHelper.toSaneSentenceFormat(social)}</ReactRouterDom.NavLink>
                        </li>))}
                    </ul>
                </div>
                {(!website.owns_copyright) && (<div>
                    <div className={styles["footer-col-title"]}>{labels.omnehost}</div>
                    <ul className={styles["footer-links"]}>
                        <li>
                            {(!!profile)
                                ? (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{labels.auth.dashboard}</ReactRouterDom.NavLink>)
                                : (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{labels.auth.sign_in}</ReactRouterDom.NavLink>)}
                        </li>
                        {labels.footer.map((f) => <li key={f.label}><ReactRouterDom.NavLink key={f.label} target="_blank" to={f.location}>{f.label}</ReactRouterDom.NavLink></li>)}
                    </ul>
                </div>)}
            </div>
            <div className={styles["footer-bottom"]}>
                <span>© {today.getFullYear()} {website.owns_copyright ? website.display_name : labels.omnehost}. {labels.landing.all_r_reserved}</span>
                <div className={styles["footer-socials"]}>
                    {Object.entries(website.owns_copyright ? (website.member.social_accounts ?? {}) : Utils.OMNEHOST_SOCIALS).map(([social, username]) => (
                        <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["footer-social-btn"]}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                    ))}
                </div>
            </div>
        </footer>
    </>);

}

export default Landing;